import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { TextInput } from '@/components/ui/TextInput'
import { SPACING } from '@/lib/brand'
import type { CardPaymentData } from '@/lib/types/payment.types'
import type { CheckoutPayer } from '@/lib/payments/charge-result'

/**
 * Campos de tarjeta compartidos por el checkout de Tours (F2) y el pago de una
 * parte de split (F3). Un solo lugar para el formato y la validación.
 */
export interface ToursCardValue {
  number: string
  expiry: string
  cvv: string
  name: string
  email: string
}

export const EMPTY_TOURS_CARD: ToursCardValue = {
  number: '',
  expiry: '',
  cvv: '',
  name: '',
  email: '',
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

export function formatCardNumber(value: string): string {
  const digits = digitsOnly(value).slice(0, 19)
  return digits.replace(/(.{4})/g, '$1 ').trim()
}

export function formatExpiry(value: string): string {
  const digits = digitsOnly(value).slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}

export function parseExpiry(value: string): { expiryMonth: number; expiryYear: number } | null {
  const digits = digitsOnly(value)
  if (digits.length !== 4) return null
  const expiryMonth = Number(digits.slice(0, 2))
  const expiryYear = Number(digits.slice(2))
  if (expiryMonth < 1 || expiryMonth > 12) return null
  return { expiryMonth, expiryYear: 2000 + expiryYear }
}

export function toursCardIsValid(value: ToursCardValue): boolean {
  return (
    digitsOnly(value.number).length >= 13 &&
    parseExpiry(value.expiry) != null &&
    digitsOnly(value.cvv).length >= 3 &&
    value.name.trim().length > 0
  )
}

export function toursCardToChargeInput(value: ToursCardValue): {
  card: CardPaymentData
  payer: CheckoutPayer
} {
  const expiry = parseExpiry(value.expiry)
  const nameParts = value.name.trim().split(' ')
  return {
    card: {
      cardNumber: digitsOnly(value.number),
      cvv: digitsOnly(value.cvv),
      expiryMonth: expiry?.expiryMonth ?? 1,
      expiryYear: expiry?.expiryYear ?? 2000,
      cardholderName: value.name.trim() || undefined,
    },
    payer: {
      email: value.email.trim(),
      firstName: nameParts[0] ?? value.name.trim(),
      lastName: nameParts.slice(1).join(' ') || 'Tago',
      phone: '',
    },
  }
}

export function ToursCardForm({
  value,
  onChange,
}: {
  value: ToursCardValue
  onChange: (next: ToursCardValue) => void
}) {
  const { t } = useTranslation()
  const set = (patch: Partial<ToursCardValue>) => onChange({ ...value, ...patch })

  return (
    <View style={{ gap: SPACING.sm }}>
      <TextInput
        label={t('tours.checkout.card_number')}
        value={value.number}
        onChangeText={(next) => set({ number: formatCardNumber(next) })}
        keyboardType="number-pad"
        placeholder="0000 0000 0000 0000"
        testID="tours.card-number"
      />
      <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
        <View style={{ flex: 1 }}>
          <TextInput
            label={t('tours.checkout.card_expiry')}
            value={value.expiry}
            onChangeText={(next) => set({ expiry: formatExpiry(next) })}
            keyboardType="number-pad"
            placeholder="MM/AA"
            testID="tours.card-expiry"
          />
        </View>
        <View style={{ flex: 1 }}>
          <TextInput
            label={t('tours.checkout.card_cvv')}
            value={value.cvv}
            onChangeText={(next) => set({ cvv: digitsOnly(next).slice(0, 4) })}
            keyboardType="number-pad"
            secureTextEntry
            placeholder="123"
            testID="tours.card-cvv"
          />
        </View>
      </View>
      <TextInput
        label={t('tours.checkout.card_name')}
        value={value.name}
        onChangeText={(next) => set({ name: next })}
        autoCapitalize="words"
        testID="tours.card-name"
      />
      <TextInput
        label={t('tours.checkout.email')}
        value={value.email}
        onChangeText={(next) => set({ email: next })}
        keyboardType="email-address"
        autoCapitalize="none"
        testID="tours.card-email"
      />
    </View>
  )
}
